import {debounce} from 'lodash'
import {useEffect, useMemo, useRef} from 'react'

import {AUTOSAVE_INTERVAL_MS} from '../consts'

interface AutoSaveValueParams<T extends string | number> {
  currentValue: T
  shouldSave: boolean
  saveFn: () => Promise<void> | void
  saveOnDestroy?: boolean
  interval?: number
}

/**
 * Hook which saves the value when it changes and shouldSave is true.
 * Debounces the save function with the given interval.
 */
export const useAutoSaveValue = <T extends string | number>({
  currentValue,
  shouldSave,
  interval = AUTOSAVE_INTERVAL_MS,
  saveOnDestroy = false,
  saveFn
}: AutoSaveValueParams<T>) => {
  const debouncedSaveFn = useMemo(() => debounce(saveFn, interval), [saveFn, interval])

  useEffect(() => {
    if (shouldSave) {
      void debouncedSaveFn()
    }
    return () => {
      debouncedSaveFn.cancel()
    }
  }, [currentValue, debouncedSaveFn, shouldSave])

  const saveOnDestroyHolder = useRef({shouldSave, saveOnDestroy, saveFn})
  useEffect(() => {
    saveOnDestroyHolder.current = {shouldSave, saveOnDestroy, saveFn}
  }, [saveOnDestroy, shouldSave, saveFn])
  useEffect(() => {
    return () => {
      const {shouldSave: save, saveOnDestroy: onDestroy, saveFn: fn} = saveOnDestroyHolder.current
      if (save && onDestroy) {
        void fn()
      }
    }
  }, [])
}
